/**
 * The filing matrix (spec 2026-09-26, section 4): which packets a scenario
 * produces, which forms each holds in page order, and how each is delivered.
 */
import {
  destinationFor1065,
  destinationFor1120,
  type DestinationId,
} from "./irs-destinations";
import type { Scenario } from "./scenario";

/** Mirrors `@nt/irs-forms`' `FormId` (kept local so this package has no deps). */
export type IrsFormId =
  | "f1120"
  | "f5472"
  | "f1120sg"
  | "f1040nr"
  | "f1040s1"
  | "f1040sc"
  | "f1040nro"
  | "f1040nrn"
  | "fw7"
  | "f1065"
  | "f1065sb1"
  | "f1065sb2"
  | "f1065sk1"
  | "f1065sk2"
  | "f1065sk3"
  | "fss4"
  | "f8995"
  | "f1040nra"
  | "f1125a";

export type PacketKind = "entity" | "individual";
export type PacketDelivery = "fax" | "mail" | "customer_mail";
export type PacketAttachment =
  "supporting_statement" | "reasonable_cause_if_late";

export interface PacketSpec {
  kind: PacketKind;
  /** FormIds in page order. A 5472 / K-1 repeats per related party / partner. */
  forms: IrsFormId[];
  /** Forms in `forms` that are included only when the return needs them. */
  optionalForms?: IrsFormId[];
  attachments: PacketAttachment[];
  delivery: PacketDelivery;
  destination: DestinationId;
}

export interface PacketsForOptions {
  /** SMLLC with ECI: the owner already has an ITIN (otherwise a W-7 goes on top). */
  hasItin?: boolean;
  /** SMLLC with ECI: the 1040-NR ends with an amount owed that is mailed with it. */
  paymentEnclosed?: boolean;
  /**
   * MMLLC and C-corp: principal business state (or "FOREIGN") and total
   * assets for the 1065 / 1120 "Where To File" tables.
   */
  principalState?: string | null;
  totalAssets?: number;
}

const ENTITY_FAX_PACKET: PacketSpec = {
  kind: "entity",
  forms: ["f1120", "f5472"],
  attachments: ["supporting_statement", "reasonable_cause_if_late"],
  delivery: "fax",
  destination: "irs_fax_foreign_owned_de",
};

/**
 * In attachment sequence order: Schedule 1 (01), Schedule A (7A), NEC (7B),
 * OI (7C), C (09), Form 8995 (55).
 */
const NR_SET: IrsFormId[] = [
  "f1040nr",
  "f1040s1",
  "f1040nra",
  "f1040nrn",
  "f1040nro",
  "f1040sc",
  "f8995",
];

export function packetsFor(
  scenario: Scenario,
  options: PacketsForOptions = {},
): PacketSpec[] {
  switch (scenario) {
    case "smllc_no_eci":
      return [{ ...ENTITY_FAX_PACKET, forms: [...ENTITY_FAX_PACKET.forms] }];
    case "smllc_eci": {
      const individual: PacketSpec = options.hasItin
        ? {
            kind: "individual",
            forms: [...NR_SET],
            optionalForms: ["f1040nra", "f1040nrn", "f8995"],
            attachments: [],
            delivery: "mail",
            destination: options.paymentEnclosed
              ? "irs_charlotte_1040nr_payment"
              : "irs_austin_1040nr",
          }
        : {
            kind: "individual",
            forms: ["fw7", ...NR_SET],
            optionalForms: ["f1040nra", "f1040nrn", "f8995"],
            attachments: [],
            delivery: "customer_mail",
            destination: "irs_itin_operation",
          };
      return [
        { ...ENTITY_FAX_PACKET, forms: [...ENTITY_FAX_PACKET.forms] },
        individual,
      ];
    }
    case "ccorp":
      return [
        {
          kind: "entity",
          // Assembling the Return (i1120 2025, page 4): Form 1125-A (item 8)
          // right after the 1120, then schedules, then forms.
          forms: ["f1120", "f1125a", "f1120sg", "f5472"],
          // A 25% foreign-owned corporation files Form 5472 only for each
          // related party it had a reportable transaction with (i5472 Rev.
          // 12-2024, line 1g); with none it files the 1120 alone. The SMLLC
          // pro forma filing is different: it always carries a 5472.
          optionalForms: ["f1125a", "f5472"],
          attachments: [],
          delivery: "mail",
          // i1120 (2025) p. 5, "Where To File". We never prepare Schedule M-3
          // (required from $10 million of total assets, i1120 2025 Schedule
          // L note), so the $10 million line comes from `totalAssets`. No
          // location given is the foreign row (see destinationFor1120).
          destination: destinationFor1120(
            options.principalState,
            options.totalAssets ?? 0,
            false,
          ),
        },
      ];
    case "mmllc":
      return [
        {
          kind: "entity",
          // Assembling the Return (i1065 2025, p. 10): Form 1125-A (item 8)
          // after pages 1-6, then the K-1s (item 16), then "any other
          // schedules in alphabetical order": B-1, B-2, K-2, K-3. The packet
          // build (apps/api packets/build.ts) assembles them in this order.
          forms: [
            "f1065",
            "f1125a",
            "f1065sk1",
            "f1065sb1",
            "f1065sb2",
            "f1065sk2",
            "f1065sk3",
          ],
          // Form 1125-A only with cost of goods sold (PG-24); Schedule B-1
          // only when a member owns 50% or more (question 2b); Schedule B-2
          // only when the partnership elects out of the BBA audit regime
          // (question 33, bba-election.ts). K-2/K-3 are not filed under the
          // 2025 Small Partnership Filing Exception (Schedule B question 4
          // "Yes"); a late or larger return files them with every part "No"
          // (PG-18, research/mmllc-k2k3.md).
          optionalForms: [
            "f1125a",
            "f1065sb1",
            "f1065sb2",
            "f1065sk2",
            "f1065sk3",
          ],
          attachments: [],
          delivery: "mail",
          // We never prepare Schedule M-3 (i1065 2025 p. 18, Item J: needed
          // from $10 million of assets or $35 million of receipts); the
          // $10 million line itself comes from `totalAssets`.
          destination: destinationFor1065(
            options.principalState,
            options.totalAssets ?? 0,
            false,
          ),
        },
      ];
  }
}
